import { useCallback, useState } from 'react';
import { getAISessionApi } from '@/api/aiChatApi';
import { store } from '@/config/store';
import { AISession } from '@/types/api/aiChat';

/** 認証切れ。API側は未ログインだとログイン画面へのリダイレクトを試みて404になる */
const HTTP_UNAUTHORIZED = 401;
const HTTP_LOGIN_REQUIRED = 404;

interface UseAISessionResult {
  session: AISession | null;
  isLoading: boolean;
  error: string | null;
  fetchSession: () => Promise<AISession | null>;
}

/**
 * AIセッション管理フック
 * セッションのキーになるログイン識別子（電話番号/メールアドレス）は
 * 認証済みの顧客からサーバ側で確定させるため、ここでは送らない。
 */
export const useAISession = (): UseAISessionResult => {
  const [session, setSession] = useState<AISession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSession = useCallback(async (): Promise<AISession | null> => {
    setIsLoading(true);
    setError(null);

    try {
      if (!store.state.authInfo?.m_customer_id) {
        throw new Error('ログインが必要です');
      }

      // authInfo はメモリ上のstoreにしか無く、リロードすると消える。
      // トークンが無いまま投げても認証ミドルウェアで404になるだけなので、
      // ここで止めて理由が分かる文言を出す
      if (!store.state.authInfo?.token) {
        throw new Error(
          'ログイン情報が失われています。再度ログインしてください。',
        );
      }

      const res = await getAISessionApi();

      if (res.data?.success && res.data.data) {
        setSession(res.data.data.session);
        return res.data.data.session;
      }

      // axios の interceptor が非2xxも resolve するため、ここに 401/404 が流れてくる。
      // 未ログイン時は認証ミドルウェアがログイン画面へのリダイレクトを試みて404になるので、
      // 「セッション取得に失敗」ではなくログインし直しを案内する
      if (
        res.status === HTTP_UNAUTHORIZED ||
        res.status === HTTP_LOGIN_REQUIRED
      ) {
        throw new Error(
          'ログインの有効期限が切れている可能性があります。再度ログインしてください。',
        );
      }

      throw new Error(
        res.data?.error_message || 'セッション取得に失敗しました',
      );
    } catch (e) {
      const errorMessage =
        e instanceof Error
          ? e.message
          : 'セッション取得中にエラーが発生しました';
      setError(errorMessage);
      console.error('AIセッション取得エラー:', e);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    session,
    isLoading,
    error,
    fetchSession,
  };
};
